/**
 * What the daily SMS cap did to a container today (user, 20/10/2026: "we
 * must be able to see that these elements cannot be sent because it will
 * hit the daily"): the slots it cut — nothing left when their turn came —
 * and the one it clamped to what was left, read from the planner's own
 * warnings (`daily_cap_starved` names the cut slots, `amount_clamped_daily_cap`
 * the clamped one). Pure; every surface reads the same answer.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenPlanWarning } from '@trafficpulse/shared';

export interface CapCuts {
  /** Slot ids the cap cut — their drops are not in today's plan. */
  cut: ReadonlySet<string>;
  /** Slot ids the cap clamped — their drop goes, smaller. */
  clamped: ReadonlySet<string>;
}

export const NO_CUTS: CapCuts = { cut: new Set(), clamped: new Set() };

export function capCutsOf(warnings: DropGenPlanWarning[] | undefined, containerId: string | null): CapCuts {
  if (!warnings || containerId === null) return NO_CUTS;
  const mine = warnings.filter((w) => w.containerId === containerId);
  const cut = new Set(mine.filter((w) => w.code === 'daily_cap_starved').flatMap((w) => w.slotIds ?? []));
  const clamped = new Set(mine.filter((w) => w.code === 'amount_clamped_daily_cap' && w.slotId).map((w) => w.slotId as string));
  return { cut, clamped };
}

/** A slot row's mark (Bugbot on #278): cut by its own id; clamped when any of its drops is —
 *  an all-lists drop after the first carries a derived slot id — with that drop's amount. */
export function capMarkOf(
  cuts: CapCuts,
  slotId: string | null,
  planned: Array<{ slotId: string | null; amount: number }>,
): { mark: 'cut' | 'clamped'; amount: number | null } | null {
  if (slotId === null) return null;
  if (cuts.cut.has(slotId)) return { mark: 'cut', amount: null };
  const hit = planned.find((d) => d.slotId !== null && cuts.clamped.has(d.slotId));
  return hit ? { mark: 'clamped', amount: hit.amount } : cuts.clamped.has(slotId) ? { mark: 'clamped', amount: planned[0]?.amount ?? null } : null;
}

/** "4 slots cut by the daily cap", "1 slot clamped by the daily cap", both, or null. */
export function capCutWords(cuts: CapCuts): string | null {
  const parts = [
    cuts.cut.size > 0 ? `${cuts.cut.size} slot${cuts.cut.size === 1 ? '' : 's'} cut` : '',
    cuts.clamped.size > 0 ? `${cuts.clamped.size} clamped` : '',
  ].filter(Boolean);
  return parts.length === 0 ? null : `${parts.join(' · ')} by the daily cap`;
}
